import { useEffect, useState } from "react";

import { useDndMonitor } from "@dnd-kit/core";
import {
  faBriefcase,
  faCalculatorSimple,
  faCube,
  faGear,
  faQuestionCircle,
  faRobot,
  faSearch,
  faTimer,
} from "@fortawesome/pro-regular-svg-icons";
import { Link, useOutletContext } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";

import { Flow } from "src/api/flowTypes";
import { Divider } from "src/base-components/Divider";
import { FeatureGated } from "src/base-components/FeatureGated";
import { Pill } from "src/base-components/Pill";
import { IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { useIsFlowDndAvailable } from "src/flowsOverview/v2/FlowDndProvider";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useIsCaseManagementEnabled } from "src/hooks/useIsCaseManagementEnabled";
import { AppHeaderLogo } from "src/layout/AppHeaderLogo";
import { ClickableSearchBox } from "src/layout/ClickableSearchBox";
import {
  DecisionFlowsNavItem,
  NavItem,
  NavSidebar,
  RetoolNavItem,
} from "src/layout/NavSideBar";
import { UserMenu } from "src/layout/UserMenuV2";
import { useOmniboxActions } from "src/omnibox/OmniboxStore";
import { useRetoolInformation } from "src/retool/Retool";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import {
  DashboardPageParams,
  getUrlToAgentsPage,
  getUrlToCaseQueuePage,
  getUrlToEventsPage,
  getUrlToSettingsPage,
  getUrlToWsDashboard,
  WsDashboardPageCode,
} from "src/router/urls";
import { useCurrentPage } from "src/router/useCurrentPage";
import { canAccessSettingsPage } from "src/settings/utils";
import { modifierKey } from "src/utils/platformModifierKey";
import { useParamsDecode } from "src/utils/useParamsDecode";

const DndSidebarMonitor: React.FC<{
  onDragChange: (isDragging: boolean) => void;
}> = ({ onDragChange }) => {
  useDndMonitor({
    onDragStart: () => onDragChange(true),
    onDragEnd: () => onDragChange(false),
    onDragCancel: () => onDragChange(false),
  });
  return null;
};

const CaseManagementNavItem = ({
  capabilities,
  orgId,
  wsId,
}: {
  capabilities: ReturnType<typeof useCapabilities>;
  orgId: string;
  wsId: string;
}) => {
  const { isEnabled: isCaseManagementEnabled } = useIsCaseManagementEnabled();

  if (!capabilities.cases.canAccess || !isCaseManagementEnabled) {
    return null;
  }

  return (
    <NavItem
      end={false}
      icon={faBriefcase}
      title="Cases"
      to={getUrlToCaseQueuePage({ orgId, wsId })}
    />
  );
};

export const MainSideBar = () => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const [collapsed, setCollapsed] = useLocalStorage(
    "mainSideBarNavigationCollapsed",
    false,
  );
  const context = useOutletContext();
  const { data: eventTypes } = useEventSchemas();
  const { flow } = context as { flow: Flow | undefined };

  const { showOmnibox } = useOmniboxActions();
  const capabilities = useCapabilities();
  const { isEntityViewPage, isFlowPage, isAuthoringPage, isSettingsPage } =
    useCurrentPage();
  const { retoolPageUrl, retoolConnection, isCurrentPageRetool } =
    useRetoolInformation();

  const shouldBeCollapsed =
    isEntityViewPage || isFlowPage || isAuthoringPage || isSettingsPage;

  const isDndAvailable = useIsFlowDndAvailable();
  const [isDragging, setIsDragging] = useState(false);
  const [isPointerOverSidebar, setIsPointerOverSidebar] = useState(false);

  const isDragHovering = isDragging && isPointerOverSidebar;
  const collapsedAndNotDragHovering =
    collapsed &&
    (!isDragHovering ||
      !isFeatureFlagEnabled(FEATURE_FLAGS.informationArchitecture));

  useEffect(() => {
    if (shouldBeCollapsed) {
      setCollapsed(true);
    }
  }, [shouldBeCollapsed, setCollapsed]);

  return (
    <div
      className="flex h-full flex-col"
      onPointerEnter={() => setIsPointerOverSidebar(true)}
      onPointerLeave={() => setIsPointerOverSidebar(false)}
    >
      {isDndAvailable && <DndSidebarMonitor onDragChange={setIsDragging} />}
      <div className="mb-5 px-3.5">
        <Link
          to={getUrlToWsDashboard({
            orgId,
            wsId,
            folderId: flow?.flow_folder_id,
          })}
        >
          <div className="mt-4 mb-7 flex items-center gap-x-2">
            <AppHeaderLogo dataLoc="header-taktile-logo" />
            {!collapsedAndNotDragHovering && (
              <p className="font-inter-semibold-13px text-gray-800">Taktile</p>
            )}
          </div>
        </Link>
        {collapsedAndNotDragHovering ? (
          <div className="py-1">
            {/* The py-1 is to account for the space added by the search box. This way the menu below does not move when the sidebar is collapsed*/}
            <Tooltip
              placement="bottom-end"
              title={
                <>
                  <span className="mr-2">Search</span>
                  <Pill size="sm" variant="dark-gray">
                    <Pill.Text fontType="text">{modifierKey} K</Pill.Text>
                  </Pill>
                </>
              }
              asChild
            >
              <IconButton icon={faSearch} size="xs" onClick={showOmnibox} />
            </Tooltip>
          </div>
        ) : (
          <ClickableSearchBox
            dataLoc="main-sidebar-search"
            text="Search"
            onClick={showOmnibox}
          />
        )}
      </div>
      <div className="px-2.5">
        <Divider />
      </div>
      <div className="min-h-0 flex-1">
        <NavSidebar
          collapsed={collapsedAndNotDragHovering}
          isDragHovering={isDragHovering}
          setCollapsed={shouldBeCollapsed ? null : setCollapsed}
          wide={isFeatureFlagEnabled(FEATURE_FLAGS.informationArchitecture)}
        >
          {capabilities.agents.canAccess &&
            isFeatureFlagEnabled(FEATURE_FLAGS.agents) && (
              <NavItem
                end={false}
                icon={faRobot}
                // Using a larger icon size for the agents icon to match the other icons in the sidebar.
                iconSize="sm"
                title="Agents"
                to={getUrlToAgentsPage({ orgId, wsId })}
              />
            )}
          <DecisionFlowsNavItem orgId={orgId} wsId={wsId} />

          <FeatureGated requiredGate="entitiesEnabled">
            <NavItem
              end={false}
              icon={faCube}
              title="Entities"
              to={getUrlToWsDashboard({
                orgId,
                wsId,
                page: WsDashboardPageCode.Entities,
              })}
            />
          </FeatureGated>

          <FeatureGated requiredGate="featuresEventsEnabled">
            <NavItem
              end={false}
              icon={faTimer}
              title="Events"
              to={getUrlToEventsPage({
                orgId,
                wsId,
                eventType: eventTypes?.at(0)?.event_type,
              })}
            />
            <NavItem
              end={false}
              icon={faCalculatorSimple}
              title="Features"
              to={getUrlToWsDashboard({
                orgId,
                wsId,
                page: WsDashboardPageCode.Features,
              })}
            />
          </FeatureGated>
          <CaseManagementNavItem
            capabilities={capabilities}
            orgId={orgId}
            wsId={wsId}
          />
          {isFeatureFlagEnabled(FEATURE_FLAGS.informationArchitecture) &&
            retoolPageUrl && (
              <RetoolNavItem
                dataLoc="retool-icon"
                isCurrentPageRetool={isCurrentPageRetool}
                retoolConnectionName={retoolConnection?.name ?? ""}
                retoolPageUrl={retoolPageUrl}
              />
            )}
          {(isFeatureFlagEnabled(FEATURE_FLAGS.informationArchitecture) ||
            canAccessSettingsPage(capabilities)) && <Divider spacing="my-2" />}

          {isFeatureFlagEnabled(FEATURE_FLAGS.informationArchitecture) && (
            <NavItem
              end={false}
              icon={faQuestionCircle}
              title="Documentation"
              to="https://docs.taktile.com"
              externalLink={true}
            />
          )}

          {canAccessSettingsPage(capabilities) && (
            <NavItem
              dataLoc="header-settings-icon"
              end={false}
              icon={faGear}
              title="Settings"
              to={getUrlToSettingsPage(orgId, wsId)}
            />
          )}

          {isFeatureFlagEnabled(FEATURE_FLAGS.informationArchitecture) && (
            <li
              className="flex items-center gap-x-2 rounded-lg p-1.5 hover:bg-gray-50"
              data-loc="main-sidebar-profile-content"
            >
              <UserMenu
                showUsername={!collapsedAndNotDragHovering}
                variant="sidebar"
              />
            </li>
          )}
        </NavSidebar>
      </div>
    </div>
  );
};
